// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import {
  Modal,
  Header,
  Segment,
  Grid,
  Label,
  Button,
  Icon,
  Message
} from 'semantic-ui-react';
import {
  EmailNotification,
  NotificationStatus,
  NotificationType,
  RecipientType
} from '../types/emailNotifications';
import { formatDateTime } from '@loa/theme';

interface EmailNotificationDetailProps {
  notification: EmailNotification;
  open: boolean;
  onClose: () => void;
  onRetry?: () => void;
}

function EmailNotificationDetail({
  notification,
  open,
  onClose,
  onRetry
}: Readonly<EmailNotificationDetailProps>) {
  const getStatusIconName = (status: NotificationStatus): 'check circle' | 'times circle' | 'clock' | 'hourglass half' => {
    switch (status) {
      case NotificationStatus.SENT:
        return 'check circle';
      case NotificationStatus.FAILED:
        return 'times circle';
      case NotificationStatus.RETRY_SCHEDULED:
        return 'clock';
      default:
        return 'hourglass half';
    }
  };

  const getStatusColor = (status: NotificationStatus): 'green' | 'red' | 'yellow' | 'blue' | 'grey' => {
    switch (status) {
      case NotificationStatus.SENT:
        return 'green';
      case NotificationStatus.FAILED:
        return 'red';
      case NotificationStatus.PENDING:
        return 'yellow';
      case NotificationStatus.RETRY_SCHEDULED:
        return 'blue';
      default:
        return 'grey';
    }
  };

  const getNotificationTypeLabel = (type: NotificationType): string => {
    switch (type) {
      case NotificationType.ADVISOR_PROGRESS:
        return 'Advisor Progress Update';
      case NotificationType.ADVISOR_COMPLETION:
        return 'Advisor Completion Notice';
      case NotificationType.PROVIDER_NOTIFICATION:
        return 'Provider LOA Notification';
      default:
        return type;
    }
  };

  const getRecipientTypeLabel = (type: RecipientType): string => {
    switch (type) {
      case RecipientType.ADVISOR:
        return 'Advisor';
      case RecipientType.PROVIDER:
        return 'Provider';
      default:
        return type;
    }
  };

  const canRetry = (): boolean => {
    return (
      (notification.status === NotificationStatus.FAILED ||
        notification.status === NotificationStatus.RETRY_SCHEDULED) &&
      notification.retryCount < notification.maxRetries
    );
  };

  return (
    <Modal open={open} onClose={onClose} size='large'>
      <Modal.Header>
        <Icon name='envelope' />
        Email Notification Details
      </Modal.Header>
      <Modal.Content scrolling>
        {/* Basic Info */}
        <Segment>
          <Header as='h3'>
            <Icon name='info circle' />
            Basic Information
          </Header>
          <Grid columns={2} divided>
            <Grid.Row>
              <Grid.Column>
                <strong>Status:</strong>
                <div style={{ marginTop: '5px' }}>
                  <Label color={getStatusColor(notification.status)}>
                    <Icon
                      name={getStatusIconName(notification.status)}
                    />
                    {notification.status}
                  </Label>
                </div>
              </Grid.Column>
              <Grid.Column>
                <strong>Notification Type:</strong>
                <div style={{ marginTop: '5px' }}>
                  <Label color='blue'>
                    {getNotificationTypeLabel(notification.notificationType)}
                  </Label>
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <strong>Recipient Type:</strong>
                <div style={{ marginTop: '5px' }}>
                  <Label>{getRecipientTypeLabel(notification.recipientType)}</Label>
                </div>
              </Grid.Column>
              <Grid.Column>
                <strong>LOA ID:</strong>
                <div style={{ marginTop: '5px' }}>
                  <code style={{ fontSize: '0.9em', color: '#666' }}>
                    {notification.loaId}
                  </code>
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>

        {/* Recipient Info */}
        <Segment>
          <Header as='h3'>
            <Icon name='user' />
            Recipient Information
          </Header>
          <Grid columns={1}>
            <Grid.Row>
              <Grid.Column>
                <strong>Email Address:</strong>
                <div style={{ marginTop: '5px', fontSize: '1.1em' }}>
                  <Icon name='mail' />
                  {notification.recipientEmail}
                </div>
              </Grid.Column>
            </Grid.Row>
            {notification.signatoryName && (
              <Grid.Row>
                <Grid.Column>
                  <strong>Signatory (for progress notifications):</strong>
                  <div style={{ marginTop: '5px' }}>
                    <Icon name='user circle' />
                    {notification.signatoryName}
                  </div>
                </Grid.Column>
              </Grid.Row>
            )}
            {notification.signedCount !== undefined &&
              notification.totalSignatories !== undefined && (
                <Grid.Row>
                  <Grid.Column>
                    <strong>Progress:</strong>
                    <div style={{ marginTop: '5px' }}>
                      <Label>
                        {notification.signedCount} of {notification.totalSignatories}{' '}
                        signatures
                      </Label>
                    </div>
                  </Grid.Column>
                </Grid.Row>
              )}
          </Grid>
        </Segment>

        {/* Timing Info */}
        <Segment>
          <Header as='h3'>
            <Icon name='clock' />
            Timing Information
          </Header>
          <Grid columns={2} divided>
            <Grid.Row>
              <Grid.Column>
                <strong>Created At:</strong>
                <div style={{ marginTop: '5px' }}>{formatDateTime(notification.createdAt)}</div>
              </Grid.Column>
              <Grid.Column>
                <strong>Created By:</strong>
                <div style={{ marginTop: '5px' }}>{notification.createdBy}</div>
              </Grid.Column>
            </Grid.Row>
            {notification.sentAt && (
              <Grid.Row>
                <Grid.Column>
                  <strong>Sent At:</strong>
                  <div style={{ marginTop: '5px', color: 'green' }}>
                    <Icon name='check circle' />
                    {formatDateTime(notification.sentAt)}
                  </div>
                </Grid.Column>
                {notification.deliveredAt && (
                  <Grid.Column>
                    <strong>Delivered At:</strong>
                    <div style={{ marginTop: '5px', color: 'green' }}>
                      <Icon name='check' />
                      {formatDateTime(notification.deliveredAt)}
                    </div>
                  </Grid.Column>
                )}
              </Grid.Row>
            )}
            {notification.status === NotificationStatus.RETRY_SCHEDULED &&
              notification.nextRetryAt && (
                <Grid.Row>
                  <Grid.Column>
                    <strong>Next Retry At:</strong>
                    <div style={{ marginTop: '5px', color: 'blue' }}>
                      <Icon name='clock' />
                      {formatDateTime(notification.nextRetryAt)}
                    </div>
                  </Grid.Column>
                  <Grid.Column />
                </Grid.Row>
              )}
            {notification.updatedAt && (
              <Grid.Row>
                <Grid.Column>
                  <strong>Last Updated:</strong>
                  <div style={{ marginTop: '5px' }}>
                    {formatDateTime(notification.updatedAt)}
                  </div>
                </Grid.Column>
                {notification.updatedBy && (
                  <Grid.Column>
                    <strong>Updated By:</strong>
                    <div style={{ marginTop: '5px' }}>{notification.updatedBy}</div>
                  </Grid.Column>
                )}
              </Grid.Row>
            )}
          </Grid>
        </Segment>

        {/* Content Preview */}
        <Segment>
          <Header as='h3'>
            <Icon name='mail' />
            Email Content
          </Header>
          <Grid columns={1}>
            <Grid.Row>
              <Grid.Column>
                <strong>Subject:</strong>
                <div
                  style={{
                    marginTop: '5px',
                    padding: '10px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                    fontSize: '1.05em'
                  }}
                >
                  {notification.subject}
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column>
                <strong>Has Attachment:</strong>
                <div style={{ marginTop: '5px' }}>
                  {notification.hasAttachment ? (
                    <>
                      <Label color='green'>
                        <Icon name='paperclip' />
                        Yes
                      </Label>
                      {notification.attachmentFilename && (
                        <span style={{ marginLeft: '10px', color: '#666' }}>
                          ({notification.attachmentFilename})
                        </span>
                      )}
                    </>
                  ) : (
                    <Label color='grey'>
                      <Icon name='minus' />
                      No
                    </Label>
                  )}
                </div>
              </Grid.Column>
            </Grid.Row>
            {notification.emailTemplateId && (
              <Grid.Row>
                <Grid.Column>
                  <strong>Template Used:</strong>
                  <div style={{ marginTop: '5px' }}>
                    <Label>
                      <Icon name='file alternate' />
                      {notification.templateLevel || 'Unknown'} Level
                    </Label>
                    <span
                      style={{
                        marginLeft: '10px',
                        fontSize: '0.85em',
                        color: '#666'
                      }}
                    >
                      ID: {notification.emailTemplateId}
                    </span>
                  </div>
                </Grid.Column>
              </Grid.Row>
            )}
          </Grid>
        </Segment>

        {/* Retry Info */}
        <Segment>
          <Header as='h3'>
            <Icon name='redo' />
            Retry Information
          </Header>
          <Grid columns={2} divided>
            <Grid.Row>
              <Grid.Column>
                <strong>Retry Count:</strong>
                <div style={{ marginTop: '5px' }}>
                  <Label>
                    {notification.retryCount} / {notification.maxRetries}
                  </Label>
                </div>
              </Grid.Column>
              <Grid.Column>
                <strong>Retries Remaining:</strong>
                <div style={{ marginTop: '5px' }}>
                  <Label color={notification.retryCount < notification.maxRetries ? 'green' : 'red'}>
                    {notification.maxRetries - notification.retryCount}
                  </Label>
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Segment>

        {/* Error Info (if failed) */}
        {notification.status === NotificationStatus.FAILED &&
          notification.errorMessage && (
            <Message negative>
              <Message.Header>
                <Icon name='exclamation triangle' />
                Error Information
              </Message.Header>
              <p style={{ marginTop: '10px', fontFamily: 'monospace', fontSize: '0.95em' }}>
                {notification.errorMessage}
              </p>
            </Message>
          )}
      </Modal.Content>
      <Modal.Actions>
        {canRetry() && onRetry && (
          <Button color='orange' onClick={onRetry}>
            <Icon name='redo' />
            Retry Now
          </Button>
        )}
        <Button primary onClick={onClose}>
          <Icon name='close' />
          Close
        </Button>
      </Modal.Actions>
    </Modal>
  );
}

export default EmailNotificationDetail;
